/* @flow */
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import cx from 'classnames';
import invariant from 'invariant';
import { OrderedMap } from 'immutable';
import * as React from 'react';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';
import URI from 'urijs';

import { ActionTypes } from 'groups/constants/GroupConstants';
import { addAnnouncementStreamSequenceItem } from 'chat/components/AnnouncementStream';
import { addStreamSequenceItem, broadcastStreamSequenceItem } from 'chat/components/ChatStream';
import { dispatcher } from 'lib/Dispatcher';
import { getFilesArrayFromFileUploads, getFirstFileId, getFirstFile } from 'lib/fileUploadHelper';
import { getNext30MinuteInterval, getDefaultScheduledTime } from 'lib/SchedulingMessage';
import { MessageItemFragment } from 'chat/components/Stream';
import {
  MODAL_FILE_CONTEXT_KEY,
  fetchFileById,
  removeFile,
  resetFile,
  uploadRequest,
} from 'reducers/fileUploads';
import { putMessageRecipientsForTargets, allTargetsAreGroups } from 'lib/RoleBasedMessagingUtils';
import { isEmpty, snakeCase, cloneDeep, pick, flatMap } from 'lodash';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import CharacterCount from 'lib/CharacterCount';
import ChatActions from 'chat/actions/ChatActions';
import Composer from './Composer.jsx';
import ComposerFilePreview from './ComposerFilePreview.jsx';
import ComposerLinkPreview from 'chat/components/ComposerLinkPreview.jsx';
import CurrentUser from 'auth/CurrentUser';
import DatePickerInput from 'components/DatePickerInput.jsx';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph.jsx';
import LanguageHoverCard from 'chat/components/LanguageHoverCard.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import Message, { TARGET_TYPES } from 'entities/Message';
import MessageActions from 'groups/actions/MessageActions';
import ModalTrigger from 'components/ModalTrigger.jsx';
import RemainingCharactersCount from './RemainingCharactersCount.jsx';
import SendToDistrictConfirmDialog from 'chat/components/SendToDistrictConfirmDialog.jsx';
import Text from 'components/Text.jsx';
import TimePickerInput from 'components/TimePickerInput.jsx';
import ToastActions from 'actions/ToastActions';
import TooltipTrigger from 'components/TooltipTrigger.jsx';
import TranslationActions from 'actions/TranslationActions';
import type { Target } from 'entities/Message';
import type { UploadRequestData } from 'reducers/fileUploads';

import './styles/VisualComposer.scss';

const scheduledSendToFewDisabledTooltipWithButton = (button: React.Node): React.Node => (
  // Tooltip trigger does not work with disabled buttons so put the trigger on a containing div.
  <TooltipTrigger tooltip="Scheduling is currently only available to classes.">
    <div>{button}</div>
  </TooltipTrigger>
);

export function fileIdFor(message: Message) {
  const attachmentId = message.getAttachmentId();
  const file = message.getFile();

  if (attachmentId) {
    // Checks for a fileId passed in through query params
    return attachmentId;
  } else if (file) {
    // Checks if the message already has an attachment (scheduled message)
    return file.id;
  }

  return null;
}

function messageItemToMessage(messageItem: *, seqNumber: string) {
  let message = cloneDeep(messageItem);
  message.id = message.uuid;

  const groupId = message.group ? message.group.id : 123456789;
  delete message['group'];
  message['group_ids'] = [groupId];
  message.threads.forEach(thread => {
    thread['group_id'] = groupId;
  });

  message['seq'] = seqNumber;

  message.linkPreviews.forEach(preview => {
    preview.image = preview.imageUrl;
    delete preview.imageUrl;
  });

  function toSnake(object, keys) {
    keys.forEach(key => {
      object[snakeCase(key)] = object[key];
      delete object[key];
    });

    return object;
  }

  message = toSnake(message, [
    'sendAt',
    'sentAt',
    'canReact',
    'createdAt',
    'publicUrl',
    'reactionSummary',
    'linkPreviews',
  ]);

  if (message && message.files && message.files.length > 0) {
    message.files.map(file => {
      file.id = file.uuid;
      return toSnake(file, ['contentType', 'previewWidth', 'previewHeight']);
    });
  }

  return message;
}

type Props = {
  autoFocus?: boolean,
  className: ?string,
  charLimit: number,
  charLimitWithFiles: number,
  constrainMaxHeight?: boolean,
  districtIsSelected?: boolean,
  initialMessage: Message,
  isEditingScheduledMessage?: boolean,
  onMessageSent: (targets: Target[]) => void,
  targets: Target[],
  onBack: Function,
  orgUuid: ?string,
  orgName: ?string,
  onRequestHide?: () => void,
  onTextChange: (text: string) => void,
  onScheduledTimeChange: (date: ?Date) => void,
  text: string,
  scheduledTime: ?Date,
  uuidForNewMessage: ?string,
  linkPreviews?: ?OrderedMap<string, *>,
  onRemoveLinkPreview?: (*) => void,

  /* redux props */
  fileUploads: OrderedMap<string, UploadRequestData>,
  fetchFileById: (fileContextKey: string, fileId: Backbone$Id) => void,
  removeFile: (fileContextKey: string, filename: string) => void,
  resetFile: (fileContextKey: string, filename: string) => void,
  uploadRequest: (fileContextKey: string, filename: string, blob: File) => void,

  putMessage: (input: Object) => Promise<Message>,
  scheduleMessage: (input: Object) => Promise<Message>,
  updateScheduledMessage: (uuid: string, input: Object) => Promise<Message>,
};

type State = {
  schedulerOpened: boolean,
  latestTranslation?: string,
};

export class VisualComposer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { initialMessage, scheduledTime } = props;

    if (initialMessage) {
      const fileId = fileIdFor(initialMessage);
      if (fileId) {
        this.props.fetchFileById(MODAL_FILE_CONTEXT_KEY, fileId);
      }
    }

    this.state = { schedulerOpened: !!scheduledTime };
  }

  handleTextChange = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    const text = event.currentTarget.value;
    const { onTextChange } = this.props;
    onTextChange(text);
  };

  handleToggleScheduler = () => {
    const { onScheduledTimeChange } = this.props;
    const { schedulerOpened } = this.state;
    const scheduledTime = schedulerOpened ? null : getDefaultScheduledTime();

    this.setState({
      schedulerOpened: !schedulerOpened,
    });

    onScheduledTimeChange(scheduledTime);
  };
  handleTranslateTo = (lang: string) => {
    const { text, onTextChange } = this.props;
    TranslationActions.translate(text, lang)
      .then(response => {
        this.setState({
          latestTranslation: lang,
        });
        onTextChange(response.translation);
      })
      .catch(errorLogger('VisualComposer translate failed'));
  };
  handleDatePickerChanged = (inputDate: Date) => {
    const { scheduledTime, onScheduledTimeChange } = this.props;

    if (scheduledTime) {
      const oldDate = new Date(scheduledTime.getTime());
      inputDate.setHours(oldDate.getHours());
      inputDate.setMinutes(oldDate.getMinutes());
      onScheduledTimeChange(inputDate);
    }
  };
  handleTimePickerChanged = (inputDate: Date) => {
    const { scheduledTime, onScheduledTimeChange } = this.props;

    if (scheduledTime) {
      const newDate = new Date(scheduledTime.getTime());
      newDate.setHours(inputDate.getHours());
      newDate.setMinutes(inputDate.getMinutes());
      onScheduledTimeChange(newDate);
    }
  };

  createMessageFromState = () => {
    const { fileUploads, targets, text, scheduledTime, uuidForNewMessage } = this.props;
    const currentUser = CurrentUser.getUser();

    return Message.createMessage(
      null, // use targets not groupcodes
      text,
      scheduledTime,
      getFilesArrayFromFileUploads(fileUploads),
      currentUser.toJSON(),
      null, // sent_at does not exist for outgoing message
      targets,
      uuidForNewMessage
    );
  };

  handleMessageSent = () => {
    const { onTextChange, onMessageSent, targets } = this.props;
    onTextChange('');
    onMessageSent(targets);
  };

  handleMessageSentError = (error: Error) => {
    ToastActions.showAPIError(error);
    const dwmPrefix = this.props.districtIsSelected ? 'DWM - ' : '';
    console.warn(error);
    console.error(`${dwmPrefix}VisualComposer sendMessage failed`);
  };

  putMessageInputFromState(senderType: string, senderUuid: string) {
    const { targets, text, fileUploads, uuidForNewMessage, linkPreviews } = this.props;
    const recipients = putMessageRecipientsForTargets(targets);
    const files = getFilesArrayFromFileUploads(fileUploads);
    const previews = linkPreviews ? linkPreviews.toList().toArray() : [];

    return {
      recipients,
      message: {
        body: text,
        fileUuids: files.map(f => f.id),
        linkPreviews: (previews.map(pre =>
          pick(pre, ['title', 'description', 'image', 'url'])
        ): Array<*>),
      },
      uuid: uuidForNewMessage,
      sender: {
        type: senderType,
        uuid: senderUuid,
      },
    };
  }

  sendMessage = () => {
    const {
      isEditingScheduledMessage,
      scheduledTime,
      uuidForNewMessage,
      initialMessage,
    } = this.props;

    const putMessageInput = this.putMessageInputFromState('user', CurrentUser.getUserUuid());
    if (scheduledTime) {
      if (isEditingScheduledMessage) {
        this.props
          .updateScheduledMessage(uuidForNewMessage || '', {
            messageInput: putMessageInput,
            sendAt: scheduledTime,
          })
          .then(() => {
            // Remove the original message in our store because send-to-few creates a new message
            dispatcher.dispatch({
              type: ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE,
              message: initialMessage,
            });
            this.handleMessageSent();
          }, this.handleMessageSentError)
          .catch(errorLogger('VisualComposer sendMessage failed'));
      } else {
        this.props
          .scheduleMessage({
            messageInput: putMessageInput,
            sendAt: scheduledTime,
          })
          .then(this.handleMessageSent, this.handleMessageSentError)
          .catch(errorLogger('VisualComposer sendMessage failed'));
      }
    } else {
      this.props
        .putMessage(putMessageInput)
        .then(this.handleMessageSent, this.handleMessageSentError)
        .catch(errorLogger('VisualComposer sendMessage failed'));
    }
  };

  editScheduledMessage() {
    const {
      initialMessage,
      isEditingScheduledMessage,
      fileUploads,
      onMessageSent,
      targets,
      text,
      scheduledTime,
      onTextChange,
    } = this.props;
    if (isEditingScheduledMessage && scheduledTime) {
      invariant(scheduledTime, 'editScheduledMessage requires scheduledTime');
      MessageActions.updateScheduledMessage(
        initialMessage,
        text,
        targets,
        getFilesArrayFromFileUploads(fileUploads),
        scheduledTime
      )
        .then(() => {
          onTextChange('');
          onMessageSent(targets);
        })
        .catch(errorLogger('VisualComposer updateScheduledMessage failed'));
    }
  }

  isValidMessage = () => {
    const { fileUploads, targets, text } = this.props;

    const isAddressed = targets && targets.length > 0;

    const allUploadsComplete = fileUploads.every(file => {
      return file.progressPercentage === 100;
    });

    const hasText = text.trim().length > 0;

    return isAddressed && allUploadsComplete && (hasText || !fileUploads.isEmpty());
  };
  isSubmitDisabled = (charactersRemaining: number) => {
    return charactersRemaining < 0 || !this.isValidMessage();
  };
  retryUpload(filename: string) {
    const { fileUploads } = this.props;
    this.props.resetFile(MODAL_FILE_CONTEXT_KEY, filename);
    const file = fileUploads.get(filename);
    invariant(file, 'VisualComposer retryUpload missing file');
    this.props.uploadRequest(MODAL_FILE_CONTEXT_KEY, filename, file.blob);
  }

  renderFilePreview() {
    const { fileUploads } = this.props;

    const firstFileId = getFirstFileId(fileUploads);
    const uploadRequestData = getFirstFile(fileUploads);
    const removeFirstFile = () =>
      firstFileId && this.props.removeFile(MODAL_FILE_CONTEXT_KEY, firstFileId);
    const retryFirstFile = () => firstFileId && this.retryUpload(firstFileId);

    return (
      uploadRequestData && (
        <div className="VisualComposer-filePreview">
          <ComposerFilePreview
            progress={uploadRequestData.progressPercentage}
            thumbnail={uploadRequestData.thumbnail}
            type={uploadRequestData.file && uploadRequestData.file.getContentType()}
            error={uploadRequestData.isError}
            onDelete={removeFirstFile}
            onRetry={retryFirstFile}
            file={uploadRequestData.file}
          />
        </div>
      )
    );
  }

  renderTranslationButton(className: ?string) {
    const { text } = this.props;
    const label = 'Translate';
    return text ? (
      <LayerTrigger
        trigger="click"
        placement="bottom"
        layer={<LanguageHoverCard onLanguagePicked={this.handleTranslateTo} />}>
        <Link
          className={className}
          eventKey="announcements.announcement_modal.universal_composer.show_translation_box.click"
          data-test-selector="visual_composer_translation_button">
          {label}
        </Link>
      </LayerTrigger>
    ) : (
      <Link className={className} disabled>
        {label}
      </Link>
    );
  }

  renderFooterPanel(charactersRemaining: number) {
    return (
      <div className="VisualComposer-footerPanel">
        {this.renderTranslationButton()}
        <div className="VisualComposer-remainingCharacterCount">
          <RemainingCharactersCount count={charactersRemaining} />
        </div>
      </div>
    );
  }

  renderScheduler() {
    const { scheduledTime } = this.props;
    const { schedulerOpened } = this.state;

    if (!schedulerOpened) {
      return this.renderScheduleButton();
    }

    const placement = 'top';
    const interval = getNext30MinuteInterval();
    const isDateInPast = !!scheduledTime && new Date() > scheduledTime;

    const datePicker = (
      <DatePickerInput
        className="VisualComposer-scheduler-input"
        value={scheduledTime}
        min={interval}
        onChange={this.handleDatePickerChanged}
        placement={placement}
      />
    );

    const timePicker = (
      <TimePickerInput
        className="VisualComposer-scheduler-input"
        error={isDateInPast}
        value={scheduledTime}
        min={interval}
        onChange={this.handleTimePickerChanged}
        placement={placement}
      />
    );

    const closeButton = this.props.isEditingScheduledMessage ? null : (
      <Button
        className="VisualComposer-scheduler-closeButton"
        level="text"
        onClick={this.handleToggleScheduler}
        aria-label="Close"
        data-test-selector="close-scheduler">
        <Glyph name="close-circle" color="brand" size="large" solo />
      </Button>
    );

    return (
      <div className="VisualComposer-scheduler" data-test-selector="visual_composer_scheduler">
        Send on {datePicker} at {timePicker}
        {closeButton}
      </div>
    );
  }

  renderScheduleButton() {
    const { targets, districtIsSelected } = this.props;
    const tryingToScheduleSendToFew = !allTargetsAreGroups(targets);

    const button = (
      <Button
        data-test-selector="visual_composer_schedule_button"
        level="secondary"
        size="small"
        weight="normal"
        onClick={this.handleToggleScheduler}
        disabled={tryingToScheduleSendToFew}>
        Schedule
      </Button>
    );

    if (districtIsSelected) {
      return null;
    } else if (tryingToScheduleSendToFew) {
      return scheduledSendToFewDisabledTooltipWithButton(button);
    } else {
      return button;
    }
  }

  renderSendButton(charactersRemaining: number) {
    const { targets, districtIsSelected, orgUuid, orgName, onRequestHide } = this.props;
    const { schedulerOpened, latestTranslation } = this.state;

    const currentURL = window.location.href;
    const pre_filled = URI(currentURL).search(true)['message'];
    const share_sdk_pre_filled_message_boolean = !isEmpty(pre_filled);

    const eventProperties = latestTranslation
      ? {
          language_code: latestTranslation,
          share_sdk_pre_filled_message: share_sdk_pre_filled_message_boolean,
        }
      : {
          share_sdk_pre_filled_message: share_sdk_pre_filled_message_boolean,
        };

    const eventKey = schedulerOpened
      ? 'announcements.announcement_modal.universal_composer.submit.schedule.click'
      : 'announcements.announcement_modal.universal_composer.submit.send.click';
    const tryingToScheduleSendToFew = schedulerOpened && !allTargetsAreGroups(targets);
    const disabled = this.isSubmitDisabled(charactersRemaining) || tryingToScheduleSendToFew;

    const sendButton = (
      <Button
        // Need VisualComposer-sendButton for functional tests
        className="VisualComposer-sendButton"
        data-test-selector="visual_composer_send_button"
        level="primary"
        size="small"
        weight="normal"
        eventKey={eventKey}
        eventProperties={eventProperties}
        disabled={disabled}
        onClick={this.sendMessage}>
        {schedulerOpened ? 'Schedule' : 'Send'}
      </Button>
    );

    const onDistrictConfirm = () => {
      const hide = onRequestHide || (() => {});

      if (!orgUuid) {
        console.error('DWM - missing district uuid - could not write district message');
        hide();
        return;
      }

      EventTracking.trackKey('announcements.composer.confirm_send.send.click', {
        organization_uuid: orgUuid,
      });

      const putMessageInput = this.putMessageInputFromState('district', orgUuid);
      this.props
        .putMessage(putMessageInput)
        .then(this.handleMessageSent, this.handleMessageSentError)
        .catch(errorLogger('DWM - VisualComposer sendMessage promise failed'));

      hide();
    };

    const orgTargets = targets.filter(
      target =>
        target.type === TARGET_TYPES.ORGANIZATION_TARGET &&
        target.selectedFilters &&
        target.selectedFilters.length
    );
    // $FlowIssue doesn't recognize array filtering on type
    const targetFilters = flatMap(orgTargets, orgTarget => orgTarget.selectedFilters);

    const continueButton = (
      <ModalTrigger
        modal={
          <SendToDistrictConfirmDialog
            filters={targetFilters}
            orgUuid={orgUuid}
            orgName={orgName}
            onConfirm={onDistrictConfirm}
          />
        }>
        <Button
          level="primary"
          size="small"
          weight="normal"
          disabled={disabled}
          eventKey="announcements.composer.continue.click"
          eventProperties={{
            organization_uuid: orgUuid,
          }}>
          Continue
        </Button>
      </ModalTrigger>
    );

    if (districtIsSelected) {
      return continueButton;
    } else if (tryingToScheduleSendToFew) {
      return scheduledSendToFewDisabledTooltipWithButton(sendButton);
    } else {
      return sendButton;
    }
  }

  renderDropzone() {
    return (
      <div className="VisualComposer-dropzone">
        <Glyph
          className="VisualComposer-dropzone-icon"
          color="eclipse"
          name="drag-and-drop"
          size="xlarge"
        />
        <Text className="VisualComposer-dropzone-text" color="eclipse" size="xxxsmall">
          Drag and drop here
        </Text>
      </div>
    );
  }

  renderLinkPreviews(linkPreviews: OrderedMap<string, *>) {
    const { onRemoveLinkPreview } = this.props;
    const items = linkPreviews.valueSeq().map(preview => {
      return (
        <ComposerLinkPreview
          key={preview.url}
          linkPreview={preview}
          onRemove={() => {
            if (onRemoveLinkPreview) {
              onRemoveLinkPreview(preview);
            }
          }}
        />
      );
    });
    return <div className="VisualComposer-contentPreviewItems">{items}</div>;
  }

  render() {
    const {
      charLimit,
      charLimitWithFiles,
      constrainMaxHeight,
      autoFocus,
      fileUploads,
      text,
      linkPreviews,
    } = this.props;
    const { schedulerOpened } = this.state;

    const charactersRemaining = CharacterCount.getRemainingCharactersForText(
      charLimit,
      charLimitWithFiles,
      text.trim(),
      !fileUploads.isEmpty()
    );

    const linkedContentSection =
      linkPreviews && linkPreviews.size > 0 ? this.renderLinkPreviews(linkPreviews) : null;

    const fileSection =
      fileUploads.isEmpty() && !linkedContentSection
        ? this.renderDropzone()
        : this.renderFilePreview();

    return (
      <div
        className={cx('VisualComposer', this.props.className)}
        data-test-selector="visual_composer_textarea">
        <div className="VisualComposer-composerWrapper">
          <div className="VisualComposer-wrap">
            <div className="VisualComposer-composerContainer">
              <Composer
                ref="composer"
                className="VisualComposer-growingComposer"
                placeholder={'Type your message'}
                autogrow
                constrainMaxHeight={constrainMaxHeight}
                value={text}
                autoFocus={autoFocus}
                error={charactersRemaining < 0}
                onChange={this.handleTextChange}>
                {this.renderFooterPanel(charactersRemaining)}
                {fileSection}
                {linkedContentSection}
              </Composer>
            </div>
            <div className="VisualComposer-buttonContainer">
              {schedulerOpened ? (
                <span />
              ) : (
                <Button
                  level="secondary"
                  weight="normal"
                  onClick={() => {
                    EventTracking.trackKey('announcements.share_to.back.click');
                    this.props.onBack();
                  }}>
                  Back
                </Button>
              )}
              <Buttons>
                {this.renderScheduler()}
                {this.renderSendButton(charactersRemaining)}
              </Buttons>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: Object) => {
  const { fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    fileUploads: fileGroup || new OrderedMap(),
  };
};

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators(
    {
      fetchFileById,
      removeFile,
      resetFile,
      uploadRequest,
    },
    dispatch
  );

const withPutMessage = graphql(
  gql`
    mutation putMessage($input: PutMessageInput!) {
      putMessage(input: $input) {
        messages {
          announcements {
            seq
            item {
              ...MessageItemFragment
            }
          }
          chatMessages {
            key
            item {
              ...MessageItemFragment
            }
          }
        }
        error {
          message
        }
      }
    }
    ${MessageItemFragment || ''}
  `,
  {
    props({ mutate }) {
      return {
        putMessage(input: Object) {
          return mutate({ variables: { input } }).then(({ data: { putMessage } }) => {
            if (putMessage.error) {
              throw new Error(putMessage.error.message);
            }

            putMessage.messages.announcements.forEach(announcement => {
              let data = cloneDeep(announcement.item);
              data = messageItemToMessage(data, announcement.seq);
              const message = Message.fromJSON(data);
              dispatcher.dispatch({
                type: ActionTypes.SEND_GROUP_MESSAGE,
                message,
              });

              try {
                if (announcement.item.sender.__typename === 'EntitySender') {
                  // the stream UUID is the sender UUID for items sent by an entity
                  const streamUUID = announcement.item.sender.uuid;
                  addAnnouncementStreamSequenceItem(
                    streamUUID,
                    {
                      key: `${announcement.item.uuid}.${announcement.seq}`,
                      __typename: 'SequenceItem',
                      item: announcement.item,
                    },
                    { overrideSequenceNumber: true }
                  );
                } else {
                  announcement.item.threads.forEach(thread => {
                    const streamUUID = thread.uuid;
                    addAnnouncementStreamSequenceItem(
                      streamUUID,
                      {
                        key: `${announcement.item.uuid}.${announcement.seq}`,
                        __typename: 'SequenceItem',
                        item: announcement.item,
                      },
                      { overrideSequenceNumber: true }
                    );
                  });
                }
              } catch (e) {
                console.debug(e);
                console.error('could not broadcast message into announcement streams');
              }

              if (announcement.item.sender.__typename !== 'EntitySender') {
                try {
                  // add the announcement into all the chat stream that
                  // are connected to the same group as the announcement
                  // if it is not sent by an entity
                  broadcastStreamSequenceItem(
                    announcement.item.group.uuid,
                    announcement.item.group.id,
                    {
                      key: `${announcement.item.uuid}.${announcement.seq}`,
                      __typename: 'SequenceItem',
                      item: announcement.item,
                    },
                    { overrideSequenceNumber: true }
                  );
                } catch (e) {
                  console.debug(e);
                  console.error('could not broadcast message into chat streams');
                }
              }
            });

            // add the chat messages into all the chat stream.
            putMessage.messages.chatMessages.forEach(item => {
              try {
                addStreamSequenceItem(item.item.chat.uuid, item, { overrideSequenceNumber: true });
              } catch (e) {
                console.debug(e);
                console.error('could not add chat message into chat stream');
              }
            });

            const needToRedirectToIndividualChat =
              putMessage.messages.announcements.length === 0 &&
              putMessage.messages.chatMessages.length === 1;
            if (needToRedirectToIndividualChat) {
              ChatActions.selectChat(putMessage.messages.chatMessages[0].item.chat.uuid);
            }
          });
        },
      };
    },
  }
);

const withScheduleMessage = graphql(
  gql`
    mutation scheduleMessage($input: ScheduleMessageInput!) {
      scheduleMessage(input: $input) {
        messages {
          announcements {
            seq
            item {
              ...MessageItemFragment
            }
          }
        }
        error {
          message
        }
      }
    }
    ${MessageItemFragment || ''}
  `,
  {
    props({ mutate }) {
      return {
        scheduleMessage(input: Object) {
          return mutate({ variables: { input } }).then(({ data: { scheduleMessage } }) => {
            if (scheduleMessage.error) {
              throw new Error(scheduleMessage.error.message);
            }

            scheduleMessage.messages.announcements.forEach(announcement => {
              let data = cloneDeep(announcement.item);
              data = messageItemToMessage(data, announcement.seq);
              const message = Message.fromJSON(data);
              dispatcher.dispatch({
                type: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE,
                message,
              });
            });
          });
        },
      };
    },
  }
);

const withUpdateScheduledMessage = graphql(
  gql`
    mutation updateScheduledMessage($uuid: String!, $input: ScheduleMessageInput!) {
      updateScheduledMessage(uuid: $uuid, input: $input) {
        messages {
          announcements {
            seq
            item {
              ...MessageItemFragment
            }
          }
        }
        error {
          message
        }
      }
    }
    ${MessageItemFragment || ''}
  `,
  {
    props({ mutate }) {
      return {
        updateScheduledMessage(uuid: string, input: Object) {
          return mutate({ variables: { uuid, input } }).then(
            ({ data: { updateScheduledMessage } }) => {
              if (updateScheduledMessage.error) {
                throw new Error(updateScheduledMessage.error.message);
              }

              updateScheduledMessage.messages.announcements.forEach(announcement => {
                let data = cloneDeep(announcement.item);
                data = messageItemToMessage(data, announcement.seq);
                const message = Message.fromJSON(data);
                dispatcher.dispatch({
                  type: ActionTypes.UPDATED_GROUP_SCHEDULED_MESSAGE,
                  message,
                });
                ToastActions.showSuccessToast('Announcement updated');
              });
            }
          );
        },
      };
    },
  }
);

export default compose(
  withPutMessage,
  withScheduleMessage,
  withUpdateScheduledMessage,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(VisualComposer);
